/**
 * MODIFIED FROM SHADCN ORIGINAL
 *
 * This component has been customized from the original shadcn/ui version.
 * Modifications: Extensively customized with CVA variants (subtle, muted, destructive, success, warning, info, message); Added AlertAction component
 *
 * Original: https://ui.shadcn.com/
 */

import { cn } from '@ophelos/ui/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

const alertVariants = cva(
  'relative w-full text-left rounded-xl border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current',
  {
    variants: {
      variant: {
        default: 'bg-card text-card-foreground',
        subtle: 'bg-soft border text-foreground [&>svg]:text-muted-foreground *:data-[slot=alert-description]:text-foreground/75',
        accent:
          'bg-accent border text-accent-foreground [&>svg]:text-accent-foreground *:data-[slot=alert-description]:text-accent-foreground/75 border-ring/25',
        muted: 'border-dashed border-border-heavy bg-muted/40 text-foreground *:data-[slot=alert-description]:text-foreground',
        destructive:
          'border-red-200 bg-red-50 text-red-800 [&>svg]:text-current *:data-[slot=alert-description]:text-red-700 dark:border-red-500/30 dark:bg-red-500/15 dark:text-red-100 [&>svg]:text-red-600 dark:[&>svg]:text-red-300 dark:*:data-[slot=alert-description]:text-red-200/90',
        success:
          'border-green-200 bg-green-50 text-green-800 [&>svg]:text-green-600 *:data-[slot=alert-description]:text-green-800 dark:border-green-500/30 dark:bg-green-500/15 dark:text-green-100 dark:[&>svg]:text-green-300 dark:*:data-[slot=alert-description]:text-green-200/90',
        warning:
          'border-amber-200 bg-amber-50 text-amber-800 *:data-[slot=alert-description]:text-amber-700/80 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-100 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-300 dark:*:data-[slot=alert-description]:text-amber-200/90',
        info: 'border-indigo-200 bg-indigo-50 text-indigo-800 [&>svg]:text-indigo-600 *:data-[slot=alert-description]:text-indigo-800 dark:border-indigo-500/30 dark:bg-indigo-500/15 dark:text-indigo-100 dark:[&>svg]:text-indigo-300 dark:*:data-[slot=alert-description]:text-indigo-200/90',
        message:
          'border-orange-200 bg-orange-50 text-orange-800 [&>svg]:text-orange-600 *:data-[slot=alert-description]:text-orange-800 dark:border-orange-500/30 dark:bg-orange-500/15 dark:text-orange-100 dark:[&>svg]:text-orange-300 dark:*:data-[slot=alert-description]:text-orange-200/90',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Alert({ className, variant, ...props }: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return <div data-slot="alert" role="alert" className={cn(alertVariants({ variant }), className)} {...props} />;
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn('col-start-2 [*:not(:has(>svg:first-child))>&]:col-start-1 line-clamp-1 min-h-4 font-medium tracking-tight', className)}
      {...props}
    />
  );
}

function AlertDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-muted-foreground col-start-2 [*:not(:has(>svg:first-child))>&]:col-start-1 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed',
        className
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-action"
      className={cn('col-start-3 row-start-1 row-span-2 self-center [*:not(:has(>svg:first-child))>&]:col-start-2', className)}
      {...props}
    />
  );
}

export { Alert, AlertAction, AlertDescription, AlertTitle };
